/**
 * AdminLayout — SaaS-style shell used by /owner/* and /hr/* pages.
 *
 * Layout:
 *   - Left sidebar (≥lg screens): logo, role-aware nav, user pill.
 *   - Mobile (<lg): top bar with hamburger toggling a slide-in drawer.
 *   - Main: header strip + children.
 */
import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ListChecks,
  Users,
  Calendar,
  Wallet,
  FileText,
  Scissors,
  BookOpen,
  RefreshCw,
  Cloud,
  Settings as SettingsIcon,
  UserPlus,
  UserMinus,
  ClipboardCheck,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Home as HomeIcon,
  Clock,
  Banknote,
  ScrollText,
  BellRing,
  BellOff,
  User as UserIcon,
  Search,
  Pin,
  TrendingUp,
  Grid3x3,
  Sparkles,
  Workflow,
  BarChart3,
  PanelLeftClose,
  PanelLeft,
  CalendarOff,
  Percent,
  Plane,
  ShieldCheck,
  Network,
  UploadCloud,
  AlertTriangle,
  CalendarClock,
  FileSignature,
  FolderKanban,
  FlaskConical, FileSpreadsheet,
} from 'lucide-react';
import { useAuthStore } from '../../lib/store';
import { logout as liffLogout } from '../../lib/liff';
import { Logo } from '../ui/Logo';
import { CloseBanner } from '../CloseBanner';
import { FeedbackWidget } from '../FeedbackWidget';
import { ChangelogWidget } from '../ChangelogWidget';
import { TenantSwitcher } from './TenantSwitcher';
import { useMenuBadgeCounts, type MenuBadgeKey } from '../../hooks';

type AdminRole = 'OWNER' | 'HR' | 'SUPERVISOR';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** If set, only these roles see the item. Otherwise visible to all admins. */
  roles?: AdminRole[];
  /** Pending-count badge key (looked up via useMenuBadgeCounts). */
  badge?: MenuBadgeKey;
  /** MANAGER maps to HR for older menu items, but a small set is OWNER/HR-only. */
  excludeManager?: boolean;
}

interface NavGroup {
  id: string;
  title: string;
  items: NavItem[];
}

// IA แบบ moodata: กลุ่ม → เมนูย่อย (ชี้ไป hub-tab จริงผ่าน #hash). หลายเมนูย่อย
// ใต้ path เดียวต้องระบุ #hash เสมอ (ไม่งั้น active state ชนกัน — ดู isActive).
const MENU: NavGroup[] = [
  {
    id: 'overview',
    title: 'ภาพรวม',
    items: [
      { to: '/owner', label: 'แดชบอร์ด', icon: LayoutDashboard, roles: ['OWNER'] },
      { to: '/hr', label: 'แดชบอร์ด', icon: LayoutDashboard, roles: ['HR'] },
      // อนุมัติคำขอ = action ข้ามโมดูล (ลา/OT/แก้ข้อมูล/ร้องเรียน) → อยู่ภาพรวม ไม่ฝังในกลุ่มเวลา
      { to: '/approvals', label: 'อนุมัติคำขอ', icon: ListChecks, badge: 'approvalsTotal' },
      { to: '/owner/approval-summary', label: 'สรุปการอนุมัติ', icon: ClipboardCheck, roles: ['OWNER', 'HR'] },
      { to: '/owner/approval-chain', label: 'ตั้งค่าสายอนุมัติ', icon: ListChecks, roles: ['OWNER'] },
      { to: '/me/team', label: 'ทีมของฉัน (ใต้บังคับบัญชา)', icon: Users },
      { to: '/me/requests', label: 'คำขอ & การอนุมัติของฉัน', icon: ListChecks },
    ],
  },
  {
    id: 'people',
    title: 'พนักงาน',
    items: [
      { to: '/hr/people#all', label: 'รายชื่อพนักงาน', icon: Users, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#onboard', label: 'เพิ่มพนักงาน', icon: UserPlus, roles: ['OWNER'] },
      { to: '/owner/register-employees', label: 'นำเข้าพนักงาน (ชื่อ+เลขบัตร)', icon: UploadCloud, roles: ['OWNER'] },
      { to: '/owner/import-historical', label: 'นำเข้าข้อมูลจากไฟล์ (พนักงาน/เงินเดือน/วันลา)', icon: UploadCloud, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#probation', label: 'ครบทดลองงาน', icon: CalendarClock, roles: ['OWNER', 'HR'] },
      { to: '/hr/contracts', label: 'สัญญาจ้าง', icon: FileSignature, roles: ['OWNER', 'HR'] },
      { to: '/hr/policies', label: 'ข้อบังคับและประกาศ', icon: BookOpen, roles: ['OWNER', 'HR'] },
      { to: '/hr/doc-kit', label: 'ชุดเอกสารบริษัท', icon: FolderKanban, roles: ['OWNER', 'HR'], excludeManager: true },
      { to: '/hr/letters', label: 'หนังสือรับรอง / หนังสือเตือน', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#termination', label: 'ลาออก / เลิกจ้าง', icon: UserMinus, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#changes', label: 'คำขอแก้ข้อมูล', icon: ListChecks, roles: ['OWNER'] },
      { to: '/owner/hr-permissions', label: 'ตั้งค่าสิทธิ์ HR', icon: ShieldCheck, roles: ['OWNER'] },
    ],
  },
  {
    // SoT (ADR 0003): foundational structure — org (departments/positions) +
    // compensation policy (bands/pay grid/benefits/total-comp/variable pay).
    id: 'structure',
    title: 'กำหนดโครงสร้างองค์กร',
    items: [
      { to: '/owner/org', label: 'โครงสร้างองค์กร', icon: Network, roles: ['OWNER', 'HR'] },
      { to: '/owner/comp', label: 'โครงสร้างค่าตอบแทน', icon: Wallet, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'time',
    title: 'เวลาทำงาน · ลา · OT',
    // เวลา/วันหยุด/สิทธิ์การลา = /hr/time ทั้งหมด รวมไว้กลุ่มเดียว (เลิกหั่นข้ามกลุ่ม 'ตั้งค่า')
    items: [
      { to: '/hr/time#today', label: 'เข้างานวันนี้', icon: Clock, roles: ['OWNER', 'HR'] },
      { to: '/hr/attendance', label: 'ประวัติเข้างานรายคน', icon: Clock, roles: ['OWNER', 'HR'] },
      { to: '/hr/escalations', label: 'กิจกรรมผิดปกติ', icon: AlertTriangle, roles: ['OWNER', 'HR'], badge: 'escalations' },
      { to: '/hr/time#shifts', label: 'กะการทำงาน', icon: Clock, roles: ['OWNER', 'HR'] },
      { to: '/hr/roster-import', label: 'นำเข้าตารางเวร', icon: UploadCloud, roles: ['OWNER', 'HR'] },
      { to: '/hr/time#holidays', label: 'ตั้งค่าวันหยุด', icon: CalendarOff, roles: ['OWNER', 'HR'] },
      { to: '/hr/time#leave', label: 'ตั้งค่าสิทธิ์การลา', icon: Plane, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'payroll',
    title: 'เงินเดือน',
    items: [
      { to: '/owner/cycle', label: 'รอบเงินเดือน', icon: Banknote, roles: ['OWNER', 'HR'] },
      { to: '/owner/rehearsal', label: 'ซ้อมทำเงินเดือน (ไม่บันทึก)', icon: FlaskConical, roles: ['OWNER', 'HR'] },
      { to: '/owner/sheet-import', label: 'นำเข้ารายการบวก/หักจากไฟล์ชีต', icon: FileSpreadsheet, roles: ['OWNER', 'HR'] },
      { to: '/owner/kcash', label: 'ไฟล์โอน K-Cash (จากชีต)', icon: Banknote, roles: ['OWNER'] },
      { to: '/owner/wage#allowance', label: 'เงินเพิ่ม / เงินหัก', icon: TrendingUp, roles: ['OWNER', 'HR'] },
      { to: '/hr/commission', label: 'คอมมิชชั่น', icon: Percent, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'reports',
    title: 'รายงาน',
    items: [
      { to: '/owner/wage#analysis', label: 'วิเคราะห์ค่าจ้าง', icon: BarChart3, roles: ['OWNER', 'HR'] },
      { to: '/owner/wage#year-end', label: 'สรุปปลายปี / 50 ทวิ', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/settings#audit', label: 'ประวัติการใช้งานระบบ', icon: ScrollText, roles: ['OWNER'] },
    ],
  },
  {
    id: 'settings',
    title: 'ตั้งค่าระบบ',
    items: [
      { to: '/owner/settings#overview', label: 'ตั้งค่าองค์กร', icon: SettingsIcon, roles: ['OWNER'] },
      // HR เข้าได้เมื่อเจ้าของเปิดสิทธิ์ edit_company — ไม่มีสิทธิ์จะเห็นหน้าอธิบายว่าขอสิทธิ์ที่ไหน (fb#176)
      { to: '/owner/settings#company', label: 'ข้อมูลบริษัท', icon: SettingsIcon, roles: ['HR'], excludeManager: true },
      { to: '/owner/notifications', label: 'ตั้งค่าการแจ้งเตือน', icon: BellOff, roles: ['OWNER', 'HR'] },
      { to: '/owner/templates', label: 'เริ่มต้นด้วยเทมเพลต', icon: Sparkles, roles: ['OWNER'] },
    ],
  },
  {
    id: 'guide',
    title: 'คู่มือ & ขั้นตอนการทำงาน',
    items: [
      { to: '/owner/blueprint', label: 'พิมพ์เขียวการทำเงินเดือน', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/guide', label: 'ขั้นตอนการทำงาน', icon: Workflow, roles: ['OWNER', 'HR'] },
      { to: '/owner/setup', label: 'ตัวช่วยตั้งค่าระบบ', icon: Sparkles, roles: ['OWNER', 'HR'] },
    ],
  },
];

const COLLAPSE_STORAGE_KEY = 'payroll-edge.menu.collapsed';

function loadCollapsedState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveCollapsedState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Optional right-side header actions (e.g. period selector, refresh button). */
  actions?: ReactNode;
  /** ซ่อนแถบ "ปิดงวด" — ใช้ในหน้าที่มี MonthlyWorkSpine แล้ว (กันซ้ำ) */
  hideCloseBanner?: boolean;
}

export function AdminLayout({ title, subtitle, children, actions, hideCloseBanner }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsedState);
  // ย่อ sidebar เป็น icon-only (จำใน localStorage)
  const [rail, setRail] = useState<boolean>(() => {
    try { return localStorage.getItem('payroll-edge.sidebar.rail') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRail((v) => {
    const n = !v;
    try { localStorage.setItem('payroll-edge.sidebar.rail', n ? '1' : '0'); } catch { /* ignore */ }
    return n;
  });
  // ค้นหา global บน topbar + กด "/" โฟกัส
  const [topSearch, setTopSearch] = useState('');
  const topSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        topSearchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  function submitTopSearch(e: React.FormEvent) {
    e.preventDefault();
    navigate(`/find${topSearch.trim() ? `?q=${encodeURIComponent(topSearch.trim())}` : ''}`);
    setTopSearch('');
  }
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const clear = useAuthStore((s) => s.clear);

  const role = profile?.role ?? 'EMPLOYEE';
  const badgeCounts = useMenuBadgeCounts(role);

  // Keep the sidebar nav scrolled where it was — AdminLayout re-mounts on
  // every navigation, which would otherwise jump the menu back to the top.
  // Callback ref so it works for both the desktop sidebar and mobile drawer
  // (the same Sidebar JSX is rendered twice).
  const navRefCb = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const saved = sessionStorage.getItem('adminNavScroll');
    if (saved) el.scrollTop = Number(saved);
    el.addEventListener('scroll', () => sessionStorage.setItem('adminNavScroll', String(el.scrollTop)), { passive: true });
  }, []);

  function isActive(to: string) {
    // แยก path กับ #hash — เมนูย่อยที่ path เดียวกันแต่ hash ต่าง ต้องไม่ active ชนกัน
    const hi = to.indexOf('#');
    const path = hi >= 0 ? to.slice(0, hi) : to;
    const hash = hi >= 0 ? to.slice(hi) : '';
    let pathMatch: boolean;
    if (path === '/owner' || path === '/hr') pathMatch = location.pathname === path;
    else pathMatch = location.pathname === path || location.pathname.startsWith(path + '/');
    if (!pathMatch) return false;
    if (hash) return location.hash === hash;
    return true;
  }

  function toggleGroup(id: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveCollapsedState(next);
      return next;
    });
  }

  function handleLogout() {
    clear();
    try { liffLogout(); } catch { /* ignore */ }
    navigate('/');
  }

  // MANAGER has the same operational access as HR → treat it as HR for nav.
  const navRole: AdminRole = role === 'MANAGER' ? 'HR' : (role as AdminRole);
  // Pre-filter items by role, drop empty groups, so we don't render headers for groups
  // with no visible items (e.g. HR shouldn't see "เงินเดือน" or "ระบบ").
  const visibleGroups = MENU
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => (!it.roles || it.roles.includes(navRole)) && !(it.excludeManager && role === 'MANAGER')),
    }))
    .filter((g) => g.items.length > 0);

  // Flat helpers for search + pinned shortcuts.
  const allItems = visibleGroups.flatMap((g) => g.items);
  const q = query.trim().toLowerCase();
  const filtered = q ? allItems.filter((it) => it.label.toLowerCase().includes(q)) : [];
  const pinned = ['/owner/cycle', '/approvals']
    .map((to) => allItems.find((it) => it.to === to))
    .filter((it): it is NavItem => !!it);

  // breadcrumb: แผนผังระบบ › กลุ่ม › หน้า (หาจากเมนูที่ตรงกับ path)
  const activeItem = allItems.find((it) => isActive(it.to));
  const activeGroup = visibleGroups.find((g) => g.items.some((it) => it.to === activeItem?.to));

  const renderItem = (item: NavItem, indent = true) => {
    const active = isActive(item.to);
    const count = item.badge ? badgeCounts[item.badge] : 0;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => setDrawerOpen(false)}
        className={`flex min-h-11 items-center gap-3 ${indent ? 'pl-5 pr-3' : 'px-3'} py-2 rounded-lg text-sm transition lg:min-h-0 ${
          active ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <item.icon className="w-4 h-4 shrink-0" />
        <span className="truncate flex-1">{item.label}</span>
        {count > 0 && (
          <span
            className={`shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
              active ? 'bg-brand-600 text-white' : 'bg-rose-500 text-white'
            }`}
            aria-label={`${count} รายการรออนุมัติ`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Link>
    );
  };

  const Sidebar = (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0">
      <div className="px-5 py-4 border-b border-gray-100">
        <Logo wordmark />
        <TenantSwitcher />
        {/* HumanAI hub shortcut — same identity, all three apps. Only shown
            to OWNER (typical multi-app role); HR/Supervisor/Employee stay
            put in PayPilot. */}
        {role === 'OWNER' && (
          <Link
            to="/launcher"
            onClick={() => setDrawerOpen(false)}
            className="mt-2 flex items-center gap-2 px-2 py-1.5 text-xs text-gray-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg"
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            เปลี่ยนแอป (HumanAI)
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-2.5 py-1.5">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหาเมนู…"
            className="bg-transparent text-sm w-full outline-none placeholder:text-gray-400"
          />
        </div>
      </div>

      <nav ref={navRefCb} className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {q ? (
          filtered.length > 0 ? (
            <div className="space-y-0.5">{filtered.map((it) => renderItem(it, false))}</div>
          ) : (
            <p className="px-3 py-6 text-sm text-gray-400 text-center">ไม่พบเมนู “{query}”</p>
          )
        ) : (
          <>
            {pinned.length > 0 && (
              <div className="pb-1">
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Pin className="w-3 h-3" /> ทางลัด
                </div>
                <div className="space-y-0.5">{pinned.map((it) => renderItem(it, false))}</div>
              </div>
            )}
            {visibleGroups.map((group) => {
              const isCollapsed = !!collapsed[group.id];
              return (
                <div key={group.id} className="pt-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50"
                  >
                    <span>{group.title}</span>
                    {isCollapsed ? (
                      <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    )}
                  </button>
                  {!isCollapsed && (
                    <div className="mt-0.5 space-y-0.5">
                      {group.items.map((item) => renderItem(item))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className={`pt-4 pb-1 ${q ? 'hidden' : ''}`}>
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            อื่นๆ
          </div>
          <Link
            to="/home"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
          >
            <HomeIcon className="w-4 h-4 shrink-0" />
            <span>เมนูพนักงาน</span>
          </Link>
          <Link
            to="/profile"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
          >
            <UserIcon className="w-4 h-4 shrink-0" />
            <span>ประวัติของฉัน</span>
          </Link>
          <Link
            to="/factsheet"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
          >
            <ScrollText className="w-4 h-4 shrink-0" />
            <span>หลักการคำนวณ</span>
          </Link>
        </div>
      </nav>

      {/* User pill */}
      <div className="border-t border-gray-100 p-3">
        <button
          onClick={toggleRail}
          className="hidden lg:flex items-center gap-2 w-full px-2 py-1.5 mb-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
        >
          <PanelLeftClose className="w-4 h-4" /> ย่อเมนู
        </button>
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-gray-50">
          <div className="w-8 h-8 rounded-full bg-brand-gradient text-white flex items-center justify-center text-sm font-bold shrink-0">
            {(profile?.display_name ?? profile?.emp_code ?? '?')[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{profile?.display_name ?? profile?.emp_code}</div>
            <div className="text-xs text-gray-500 truncate">
              {profile?.role} · {profile?.emp_code}
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="ออกจากระบบ"
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );

  // sidebar แบบย่อ (icon-only) สำหรับ desktop
  const railSidebar = (
    <aside className="w-16 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0 items-center">
      <div className="py-4 border-b border-gray-100 w-full flex justify-center">
        <Logo />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 w-full flex flex-col items-center gap-1">
        {allItems.map((it) => {
          const active = isActive(it.to);
          const count = it.badge ? badgeCounts[it.badge] : 0;
          return (
            <Link
              key={it.to}
              to={it.to}
              title={it.label}
              aria-label={it.label}
              className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition ${
                active ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <it.icon className="w-5 h-5" />
              {count > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-gray-100 p-2 w-full flex justify-center">
        <button onClick={toggleRail} title="ขยายเมนู" aria-label="ขยายเมนู" className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
          <PanelLeft className="w-5 h-5" />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar (ย่อได้) */}
      <div className="hidden lg:block shrink-0">{rail ? railSidebar : Sidebar}</div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-40 lg:hidden transform transition-transform ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {Sidebar}
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-4 lg:px-8 py-3 flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 lg:hidden"
            >
              {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="p-1 rounded-lg hover:bg-gray-100 hidden lg:block"
              title="ย้อนกลับ"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              {/* breadcrumb — แผนผังระบบ / กลุ่ม / หน้า */}
              <nav className="hidden lg:flex items-center gap-1 text-xs text-gray-400 mb-0.5 truncate" aria-label="breadcrumb">
                <Link to={role === 'OWNER' ? '/owner' : '/hr'} className="hover:text-gray-600">แผนผังระบบ</Link>
                {activeGroup && <><ChevronRight className="w-3 h-3" /><span>{activeGroup.title}</span></>}
                <ChevronRight className="w-3 h-3" /><span className="text-gray-600 truncate">{title}</span>
              </nav>
              <h1 className="text-lg font-semibold truncate">{title}</h1>
              {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
            </div>

            {/* ค้นหา global (กด / โฟกัส) — desktop */}
            <form onSubmit={submitTopSearch} className="hidden lg:flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 w-72 shrink-0">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                ref={topSearchRef}
                value={topSearch}
                onChange={(e) => setTopSearch(e.target.value)}
                placeholder="ค้นหาพนักงาน เมนู หน้า…"
                className="bg-transparent text-sm w-full outline-none placeholder:text-gray-400"
              />
              <kbd className="text-[10px] text-gray-400 border border-gray-300 rounded px-1 shrink-0">/</kbd>
            </form>

            {actions && <div className="flex items-center gap-1.5 shrink-0 max-w-[40%] overflow-x-auto">{actions}</div>}
          </div>
        </header>

        {!hideCloseBanner && <CloseBanner />}

        {/* fb#464 — pb-24 เผื่อปุ่มลอยมุมล่าง (อัปเดต/แจ้งปัญหา) ไม่บังปุ่มท้ายหน้า เช่น บันทึก บนมือถือ */}
        <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-24 max-w-7xl">{children}</div>
      </main>

      {/* แจ้งปัญหา / ขอฟีเจอร์ — มุมขวาล่าง (เฉพาะ HR/Owner, ตรวจ role ในตัว widget) */}
      <FeedbackWidget />

      {/* รายการอัปเดตระบบ — มุมซ้ายล่าง (โชว์เฉพาะหน้าแอดมินผ่าน AdminLayout) */}
      <ChangelogWidget />
    </div>
  );
}
